import gate from '@/api/axios';

/**
 * Получает конфигурацию
 * @async
 * @function configs
 * @returns {Promise}
 */
export const configs = async () => gate.get('/configs');

/**
 * Обновляет конфигурацию
 * @async
 * @function updateConfigs
 * @param {Object} payload
 * @returns {Promise}
 */
export const updateConfigs = async (payload) => gate.patch('/configs', {
  ...payload,
});

/**
 * Получает настройки длительности статусов заказов
 * @async
 * @function orderTimers
 * @returns {Promise}
 */
export const orderTimers = async () => gate.get('/configs/orderTimers');

/**
 * Редактирует настройки длительности статусов заказов
 * @async
 * @function updateOrderTimers
 * @param {Object} payload
 * @returns {Promise}
 */
export const updateOrderTimers = async (payload) => gate.patch('/configs/orderTimers', payload);

/**
 * Получает список стран
 * @async
 * @function countries
 * @returns {Promise}
 */
export const countries = async () => gate.get('/countries');

/**
 * Получает список регионов
 * @async
 * @function regions
 * @param {Number|String} id ID страны
 * @returns {Promise}
 */
export const regions = async (id) => gate.get(`/regions/${id}`);

/**
 * Получает список доступных регионов для регистрации самозанятых
 * @async
 * @function individualRegions
 * @returns {Promise}
 */
export const individualRegions = async () => gate.get('/workRegions/allowedForIndividualContractors');

/**
 * Получает список районов
 * @async
 * @function districts
 * @param {Number|String} id ID региона
 * @returns {Promise}
 */
export const districts = async (id) => gate.get(`/districts/${id}`);

/**
 * Получает список кладбищ
 * @async
 * @function cemeteries
 * @param {Number|String} id ID района
 * @returns {Promise}
 */
export const cemeteries = async (id) => gate.get(`/cemeteries/${id}`);

/**
 * Получает список регионов для работы
 * @async
 * @function workingRegions
 * @returns {Promise}
 */
export const workingRegions = async () => gate.get('/workRegions');

/**
 * Активация и деактивация региона работы
 * @async
 * @function setWorkingRegions
 * @param {Number|String} id ID региона работы
 * @param {Number|Boolean} value
 * @returns {Promise}
 */
export const setWorkingRegions = async (id, value) => gate.post(`/workRegions/${id}/setActive`, {
  active: !!value,
});

/**
 * Получает список администраторов
 * @async
 * @function fetchAdmins
 * @returns {Promise}
 */
export const fetchAdmins = async () => gate.get('/admins');

/**
 * Создание администратора
 * @async
 * @function createAdmin
 * @param {Object} payload
 * @returns {Promise}
 */
export const createAdmin = async (payload) => gate.post('/admins', payload);

/**
 * Редактирование прав администратора
 * @async
 * @function updatePermissions
 * @param {Number|String} id ID администратора
 * @param {Object} permissions
 * @returns {Promise}
 */
export const updatePermissions = async (id, permissions) => gate.patch(`/admins/${id}`, {
  permissions,
});


/**
 * Admin Access V2: profile/role/permission update.
 * The caller may also include the legacy permissions object for rolling deploy compatibility.
 */
export const updateAdminAccess = async (id, payload) => gate.patch(`/admins/${id}`, payload);

/**
 * Редактирование профиля администратора
 * @async
 * @function updateAdminProfile
 * @param {Number|String} id ID администратора
 * @param {Object} payload
 * @returns {Promise}
 */
export const updateAdminProfile = async (id, payload) => gate.patch(`/admins/${id}`, {
  ...payload,
});

/**
 * Активация / деактивация администратора
 * @async
 * @function setAdminActive
 * @param {Number|String} id ID администратора
 * @param {Boolean} active
 * @returns {Promise}
 */
export const setAdminActive = async (id, active) => gate.post(`/admins/${id}/setActive`, {
  active: !!active,
});

/**
 * Сброс данных авторизации администратора
 * @async
 * @function resetAdminSecureCredentials
 * @param {Number|String} id ID администратора
 * @returns {Promise}
 */
export const resetAdminSecureCredentials = async (id) => gate.post(`/admins/${id}/resetSecureCredentials`);

/**
 * Удаление администратора
 * @async
 * @function deleteAdmin
 * @param {Number|String} id ID администратора
 * @returns {Promise}
 */
export const deleteAdmin = async (id) => gate.delete(`/admins/${id}`);

/**
 * Получает сводку состояния текущего окружения iWALY.
 * @async
 * @function monitoringDashboard
 * @param {String} range 1h | 24h | 7d | 30d
 * @returns {Promise}
 */
export const monitoringDashboard = async (range = '24h') => gate.get('/monitoring/dashboard', {
  params: { range },
});

/**
 * Получает подробности инцидента мониторинга.
 * @async
 * @function monitoringIncident
 * @param {Number|String} id ID инцидента
 * @returns {Promise}
 */
export const monitoringIncident = async (id) => gate.get(`/monitoring/incidents/${id}`);

/**
 * Отмечает инцидент мониторинга как просмотренный администратором.
 * @async
 * @function acknowledgeMonitoringIncident
 * @param {Number|String} id ID инцидента
 * @returns {Promise}
 */
export const acknowledgeMonitoringIncident = async (id) => gate.post(`/monitoring/incidents/${id}/acknowledge`);

/**
 * Отправляет тестовое системное уведомление в Telegram и MAX.
 * @async
 * @function testMonitoringNotification
 * @returns {Promise}
 */
export const testMonitoringNotification = async () => gate.post('/monitoring/test-notification');


/**
 * Business Analytics V1 dashboard.
 * @param {Object} params Период, сравнение и бизнес-фильтры.
 * @returns {Promise}
 */
export const analyticsDashboard = async (params = {}) => gate.get('/analytics/dashboard', { params });

/**
 * Получает текущие редакции правовых документов.
 * Тексты документов в админке не хранятся — только номер/дата активной редакции.
 * @returns {Promise}
 */
export const legalDocuments = async () => gate.get('/configs/legalDocuments');

/**
 * Публикует новую неизменяемую редакцию правового документа.
 * Предыдущая редакция остаётся в БД как история и становится неактивной.
 * @param {String} documentType license | public_offer | policy
 * @param {Object} payload { version, revisionDate }
 * @returns {Promise}
 */
export const publishLegalDocumentRevision = async (documentType, payload) => gate.post(
  `/configs/legalDocuments/${documentType}/revisions`,
  payload,
);


/**
 * Получает настройки маршрутизации SMS-провайдеров.
 * Секреты провайдеров backend не возвращает.
 * @returns {Promise}
 */
export const smsSettings = async () => gate.get('/configs/sms');

/**
 * Обновляет основной/резервный SMS-провайдер.
 * @param {Object} payload
 * @returns {Promise}
 */
export const updateSmsSettings = async (payload) => gate.patch('/configs/sms', payload);

/**
 * Получает живой статус и баланс SMS-провайдеров.
 * @param {Boolean} refresh Принудительно обойти минутный cache backend
 * @returns {Promise}
 */
export const smsProviderStatus = async (refresh = false) => gate.get('/configs/sms/status', {
  params: { refresh: refresh ? 1 : 0 },
});

/**
 * Получает безопасные настройки AI-диагностики мониторинга.
 * API-ключи намеренно не возвращаются в браузер.
 * @returns {Promise}
 */
export const monitoringAiSettings = async () => gate.get('/monitoring/ai-settings');

/**
 * Сохраняет динамические настройки AI-диагностики мониторинга.
 * @param {Object} payload
 * @returns {Promise}
 */
export const updateMonitoringAiSettings = async (payload) => gate.patch('/monitoring/ai-settings', payload);

/**
 * Скачивает AI-friendly диагностический пакет мониторинга за 1 / 7 / 30 дней.
 * @param {Number} days
 * @returns {Promise}
 */
export const monitoringDiagnosticExport = async (days = 7) => gate.get('/monitoring/diagnostic-export', {
  params: { days },
  responseType: 'blob',
});

/**
 * Email control: audit, preferences, suppressions and delivery history.
 */
export const emailControl = async (params = {}) => gate.get('/email-control', { params });

export const suppressEmail = async (payload) => gate.post('/email-control/suppressions', payload);

export const unsuppressEmail = async (id) => gate.patch(`/email-control/suppressions/${id}/disable`);

export const updateEmailPreferences = async (userId, payload) => gate.patch(
  `/email-control/users/${userId}/preferences`,
  payload,
);

/**
 * Редактируемые шаблоны быстрых/умных ответов и системной Email-подписи.
 */
export const supportMessageTemplates = async () => gate.get('/configs/supportTemplates');

export const updateSupportMessageTemplate = async (key, payload) => gate.patch(
  `/configs/supportTemplates/${encodeURIComponent(key)}`,
  payload,
);

export const resetSupportMessageTemplate = async (key) => gate.delete(
  `/configs/supportTemplates/${encodeURIComponent(key)}`,
);
